/**
 * Bookmark toggle on shortcut rows. Stop pointer events so Radix DropdownMenuItem
 * does not select the row (pointer-up synthesises click if pointer-down was stopped).
 */

import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import { Bookmark } from 'lucide-react';
import { useIntl } from 'react-intl';

interface ShortcutSaveToggleProps {
  isSaved: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const ShortcutSaveToggle = ({
  isSaved,
  onToggle,
  disabled = false,
}: ShortcutSaveToggleProps) => {
  const { t } = useTranslations();
  const intl = useIntl();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled && !isSaved) return;
    onToggle();
  };

  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={handleClick}
      disabled={disabled && !isSaved}
      aria-label={
        isSaved
          ? t('floatingAskHeidiShortcuts.removeShortcutTooltip')
          : intl.formatMessage({
              defaultMessage: 'Save shortcut',
              id: '6LxwnUXQpD',
              description:
                'Aria-label for the bookmark toggle that saves a recommended shortcut',
            })
      }
      className={cn(
        'ms-auto inline-flex size-6 shrink-0 items-center justify-center rounded-md text-text-tertiary transition-opacity',
        'hover:bg-surface-hover hover:text-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-pressed',
        isSaved
          ? 'text-text-secondary opacity-100'
          : [
              'opacity-0 group-hover:opacity-100 group-focus:opacity-100',
              'focus-visible:opacity-100 group-data-[highlighted]:opacity-100',
              disabled && 'cursor-not-allowed group-hover:opacity-40',
            ]
      )}
    >
      <Bookmark size={14} {...(isSaved ? { fill: 'currentColor' } : {})} />
    </button>
  );
};
